import {
  type CreateDatabaseProps,
  fetchBackupRp,
  fetchDatabaseImages,
  provisionDatabase,
  selectDatabaseImagesVisible,
  selectEnvironmentById,
  selectLatestBackupRpByEnvId,
} from "@app/deploy";
import { generateHash } from "@app/id";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import {
  environmentActivityUrl,
  environmentBackupsUrl,
  environmentDatabasesUrl,
} from "@app/routes";
import { defaultDeployDisk, defaultDeployService } from "@app/schema";
import {
  diskSizeValidator,
  existValidator,
  handleValidator,
} from "@app/validator";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDatabaseScaler, useValidator } from "../hooks";
import { EnvironmentDetailLayout } from "../layouts";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  ButtonCreate,
  ContainerProfileInput,
  ContainerSizeInput,
  CpuShareView,
  DatabaseNameInput,
  DiskSizeInput,
  FormGroup,
  IopsInput,
  Select,
  type SelectOption,
  ServicePricingCalc,
} from "../shared";
import { EnvSelectorPage } from "./create-env-app";

const validators = {
  handle: (props: CreateDatabaseProps) => handleValidator(props.handle),
  diskSize: (props: CreateDatabaseProps) => diskSizeValidator(props.diskSize),
  databaseImageId: (props: CreateDatabaseProps) =>
    existValidator(props.databaseImageId, "must pick database"),
};

export const CreateDatabasePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const envId = params.get("environment_id") || "";
  const env = useSelector((s) => selectEnvironmentById(s, { id: envId }));

  useQuery(fetchBackupRp({ envId }));
  const backupRp = useSelector((s) =>
    selectLatestBackupRpByEnvId(s, { envId }),
  );
  const [errors, validate] = useValidator<
    CreateDatabaseProps,
    typeof validators
  >(validators);

  const imgLoader = useQuery(fetchDatabaseImages());
  const dbImages = useSelector(selectDatabaseImagesVisible);
  const imgOptions = [
    { value: "", label: "Choose a Database" },
    ...dbImages.map((img) => ({
      label: img.description,
      value: img.id,
    })),
  ];
  const [imageId, setImageId] = useState("");
  const [dbName, setDbName] = useState("");
  const imgSelected = dbImages.find((i) => i.id === imageId);
  const selectChange = (option: SelectOption) => {
    const imgId = option.value;
    const img = dbImages.find((i) => i.id === imgId);
    const namePrefix = `${env.handle}-${generateHash(5)}`;
    const name = `${namePrefix}-${img?.type || ""}`;
    setImageId(imgId);
    setDbName(name);
  };

  const service = defaultDeployService();
  const disk = defaultDeployDisk();
  const { scaler, dispatchScaler, requestedContainerProfile } =
    useDatabaseScaler({
      service,
      disk,
    });

  const createProps: CreateDatabaseProps = {
    envId,
    type: imgSelected?.type || "",
    handle: dbName,
    databaseImageId: imageId,
    enableBackups: true,
    ...scaler,
  };
  const action = provisionDatabase(createProps);
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate(createProps)) return;
    dispatch(action);
  };
  const loader = useLoader(action);
  useLoaderSuccess(loader, () => {
    navigate(environmentActivityUrl(envId));
  });

  // Elasticsearch requires at least 1GB to start up
  const minContainerSize = imgSelected?.type === "elasticsearch" ? 1024 : 0;
  useEffect(() => {
    if (imgSelected?.type === "elasticsearch") {
      if (scaler.containerSize < 1024) {
        dispatchScaler({
          type: "containerSize",
          payload: 1024,
        });
      }
    }
  }, [imgSelected?.type, scaler.containerSize]);

  if (!envId) {
    return (
      <EnvSelectorPage
        onSuccess={(p) => {
          setParams(
            { stack_id: p.stackId, environment_id: p.envId },
            { replace: true },
          );
        }}
      />
    );
  }

  return (
    <EnvironmentDetailLayout>
      <Box>
        <form onSubmit={onSubmit}>
          <div className="flex flex-col gap-4">
            <FormGroup htmlFor="new-db" label="Database Type">
              <Select
                id="new-db"
                ariaLabel="new-db"
                onSelect={selectChange}
                value={imageId}
                options={imgOptions}
                className="flex-1 mr-2"
                disabled={imgLoader.isLoading}
              />
            </FormGroup>
            <DatabaseNameInput
              value={createProps.handle}
              onChange={(value) => setDbName(value)}
              feedbackMessage={errors.handle}
              feedbackVariant={errors.handle ? "danger" : "default"}
            />
            <ContainerProfileInput
              scaler={scaler}
              dispatchScaler={dispatchScaler}
              envId={envId}
            />
            <DiskSizeInput
              scaler={scaler}
              dispatchScaler={dispatchScaler}
              error={errors.diskSize}
            />
            <IopsInput scaler={scaler} dispatchScaler={dispatchScaler} />
            <ContainerSizeInput
              scaler={scaler}
              dispatchScaler={dispatchScaler}
              minSize={minContainerSize}
            />
            <CpuShareView
              cpuShare={requestedContainerProfile.cpuShare}
              containerSize={scaler.containerSize}
            />
            <ServicePricingCalc
              service={{
                containerCount: 1,
                containerMemoryLimitMb: scaler.containerSize,
                instanceClass: scaler.containerProfile,
              }}
              disk={{
                size: scaler.diskSize,
                provisionedIops: scaler.iops,
              }}
            />
          </div>

          <hr className="my-4" />

          {imgSelected?.pitrSupported && !backupRp.pitrDays && (
            <Banner className="mb-4" variant="warning">
              <p>
                Point-in-time Recovery (PITR) will not be automatically enabled
                since this{" "}
                <a
                  href={environmentBackupsUrl(envId)}
                  rel="noreferrer"
                  className="underline"
                >
                  environment's backup retention policy
                </a>{" "}
                for recovery data is currently set to 0 days.
              </p>
            </Banner>
          )}

          <BannerMessages className="mb-4" {...loader} />

          <div className="flex gap-2">
            <ButtonCreate
              envId={envId}
              type="submit"
              isLoading={loader.isLoading}
              disabled={imageId === ""}
            >
              Save
            </ButtonCreate>

            <Button
              variant="white"
              onClick={() => navigate(environmentDatabasesUrl(envId))}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Box>
    </EnvironmentDetailLayout>
  );
};
